import { Fragment, useEffect, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Download, Plus, Save, X } from "lucide-react";
import { Spinner } from "@/components/ui/Spinner";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { mileageSheetApi, type MileageDay, type MileageTrip } from "../api/finances";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { useAuthStore } from "@/store/auth";
import { useEmployeeExpenseProxyTargets } from "../hooks/useFinances";
import { MONTH_NAMES } from "@/features/timesheets/types";
import { downloadBlob } from "@/utils/download";
import { formatDateTime } from "@/utils/formatDate";

const CURRENT_YEAR = new Date().getFullYear();
const CURRENT_MONTH = new Date().getMonth() + 1;

function buildPeriods() {
  const periods: { year: number; month: number; label: string }[] = [];
  for (let i = 2; i >= 0; i--) {
    let m = CURRENT_MONTH - i;
    let y = CURRENT_YEAR;
    if (m <= 0) { m += 12; y -= 1; }
    periods.push({ year: y, month: m, label: `${MONTH_NAMES[m]} ${y}` });
  }
  return periods;
}
const PERIODS = buildPeriods();

const emptyTrip = (): MileageTrip => ({
  purpose: "",
  from_location: "",
  to_location: "",
  kilometers: "",
  odometer: "",
  notes: "",
});

interface Props {
  open: boolean;
  onClose: () => void;
}

export function MileageSheetModal({ open, onClose }: Props) {
  const user = useAuthStore((s) => s.user);
  const canProxy =
    user?.role === "director" ||
    user?.role === "accountant" ||
    user?.role === "coordinator" ||
    !!user?.is_ceo;

  const [year, setYear] = useState(CURRENT_YEAR);
  const [month, setMonth] = useState(CURRENT_MONTH);
  const [proxyOn, setProxyOn] = useState(false);
  const [proxyEmployeeId, setProxyEmployeeId] = useState("");
  const [localTrips, setLocalTrips] = useState<Record<number, MileageTrip[]>>({});
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState("");
  const initPeriodRef = useRef("");

  const queryClient = useQueryClient();

  // Generating on behalf of another employee (not the one the sheet concerns).
  const isProxy = proxyOn && !!proxyEmployeeId;
  const targetEmployee = isProxy ? proxyEmployeeId : undefined;

  const { data: proxyTargets = [] } = useEmployeeExpenseProxyTargets(open && canProxy);

  const { data, isLoading } = useQuery({
    queryKey: ["mileage-sheet", year, month, targetEmployee ?? "self"],
    queryFn: () => mileageSheetApi.get(year, month, targetEmployee),
    enabled: open && (!proxyOn || !!proxyEmployeeId),
    staleTime: 0,
  });

  const saveMutation = useMutation({
    mutationFn: () =>
      mileageSheetApi.save({
        year,
        month,
        days: (data?.days ?? []).map((d) => ({
          day: d.day,
          trips: (localTrips[d.day] ?? []).filter(
            (t) => t.purpose || t.from_location || t.to_location || t.kilometers,
          ),
        })),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mileage-sheet", year, month] });
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    },
    onError: () => setError("Błąd podczas zapisywania. Spróbuj ponownie."),
  });

  useEffect(() => {
    if (!data?.days) return;
    const periodKey = `${year}-${month}-${targetEmployee ?? "self"}`;
    if (initPeriodRef.current === periodKey) return;
    initPeriodRef.current = periodKey;

    const trips: Record<number, MileageTrip[]> = {};
    data.days.forEach((d: MileageDay) => {
      trips[d.day] = d.trips.length > 0 ? d.trips.map((t) => ({ ...t })) : [];
    });
    setLocalTrips(trips);
  }, [data, year, month, targetEmployee]);

  const addTrip = (day: number) => {
    setLocalTrips((prev) => ({
      ...prev,
      [day]: [...(prev[day] ?? []), emptyTrip()],
    }));
  };

  const removeTrip = (day: number, idx: number) => {
    setLocalTrips((prev) => ({
      ...prev,
      [day]: (prev[day] ?? []).filter((_, i) => i !== idx),
    }));
  };

  const updateTrip = (day: number, idx: number, field: keyof MileageTrip, value: string) => {
    setLocalTrips((prev) => {
      const trips = [...(prev[day] ?? [])];
      trips[idx] = { ...trips[idx], [field]: value };
      return { ...prev, [day]: trips };
    });
  };

  const downloadPdf = async () => {
    if (!data?.days) return;
    setDownloading(true);
    setError("");
    try {
      // Build the payload: days with current local trips merged in
      const days: MileageDay[] = data.days.map((d) => ({
        ...d,
        trips: (localTrips[d.day] ?? []).filter(
          (t) => t.purpose || t.from_location || t.to_location || t.kilometers,
        ),
      }));
      const blob = await mileageSheetApi.exportPdf({ year, month, days, employee: targetEmployee });
      downloadBlob(blob, `kilometrowka_${year}_${String(month).padStart(2, "0")}.pdf`);
    } catch {
      setError("Błąd podczas generowania PDF. Spróbuj ponownie.");
    } finally {
      setDownloading(false);
    }
  };

  const inputCls =
    "w-full rounded border border-gray-200 dark:border-gray-600 px-1.5 py-0.5 text-xs bg-white dark:bg-gray-700 focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)] placeholder-gray-300 dark:placeholder-gray-600";

  return (
    <Modal open={open} onClose={onClose} title="Kilometrówka" size="xl">
      <div className="flex flex-col gap-4">

        {/* Period selector */}
        <div className="flex items-end gap-4">
          <div className="min-w-44">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
              Miesiąc
            </label>
            <select
              className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
              value={`${year}-${month}`}
              onChange={(e) => {
                const [y, m] = e.target.value.split("-").map(Number);
                initPeriodRef.current = "";
                setSaveSuccess(false);
                setYear(y);
                setMonth(m);
                setLocalTrips({});
              }}
            >
              {PERIODS.map((p) => (
                <option key={`${p.year}-${p.month}`} value={`${p.year}-${p.month}`}>
                  {p.label}
                </option>
              ))}
            </select>
          </div>
          <p className="text-xs text-gray-400 dark:text-gray-500 pb-2">
            Kliknij <span className="font-semibold">+ dodaj</span> przy danym dniu, aby dodać przejazd.
          </p>
        </div>

        {/* On-behalf generation */}
        {canProxy && proxyTargets.length > 0 && (
          <div className="border-b border-gray-100 dark:border-gray-700 pb-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={proxyOn}
                onChange={(e) => {
                  setProxyOn(e.target.checked);
                  setProxyEmployeeId("");
                  initPeriodRef.current = "";
                  setLocalTrips({});
                  setSaveSuccess(false);
                  setError("");
                }}
                className="w-4 h-4 accent-[var(--color-primary)]"
              />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Generuj za pracownika (w jego imieniu)
              </span>
            </label>
            {proxyOn && (
              <div className="mt-2 max-w-sm">
                <SearchableSelect
                  placeholder="— wybierz pracownika —"
                  value={proxyEmployeeId}
                  onChange={(e) => {
                    setProxyEmployeeId(e.target.value);
                    initPeriodRef.current = "";
                    setLocalTrips({});
                    setSaveSuccess(false);
                    setError("");
                  }}
                  options={proxyTargets.map((t) => ({
                    value: t.id,
                    label: t.is_active ? t.full_name : `${t.full_name} (nieaktywny)`,
                  }))}
                />
              </div>
            )}
          </div>
        )}

        {/* Days table */}
        {isLoading ? (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        ) : data?.days ? (
          <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded max-h-[480px] overflow-y-auto">
            <table className="w-full text-xs border-collapse" style={{ minWidth: 1000 }}>
              <thead className="sticky top-0 z-10">
                <tr className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                  <th className="px-2 py-2 text-left w-14 font-mono text-[10px] uppercase tracking-widest text-gray-400 dark:text-gray-500">
                    Data
                  </th>
                  <th className="px-2 py-2 text-left w-9 font-mono text-[10px] uppercase tracking-widest text-gray-400 dark:text-gray-500">
                    Dz.
                  </th>
                  <th className="px-2 py-2 text-left font-mono text-[10px] uppercase tracking-widest text-gray-400 dark:text-gray-500">
                    Cel wyjazdu
                  </th>
                  <th className="px-2 py-2 text-left w-28 font-mono text-[10px] uppercase tracking-widest text-gray-400 dark:text-gray-500">
                    Skąd
                  </th>
                  <th className="px-2 py-2 text-left w-28 font-mono text-[10px] uppercase tracking-widest text-gray-400 dark:text-gray-500">
                    Dokąd
                  </th>
                  <th className="px-2 py-2 text-right w-20 font-mono text-[10px] uppercase tracking-widest text-gray-400 dark:text-gray-500">
                    km
                  </th>
                  <th className="px-2 py-2 text-right w-28 font-mono text-[10px] uppercase tracking-widest text-gray-400 dark:text-gray-500">
                    Licznik
                  </th>
                  <th className="px-2 py-2 text-left font-mono text-[10px] uppercase tracking-widest text-gray-400 dark:text-gray-500">
                    Uwagi
                  </th>
                  {!isProxy && (
                    <th className="px-2 py-2 text-left w-24 font-mono text-[10px] uppercase tracking-widest text-gray-400 dark:text-gray-500">
                      Ost. zmiana
                    </th>
                  )}
                  <th className="px-2 py-2 w-12" />
                </tr>
              </thead>
              <tbody>
                {data.days.map((d: MileageDay) => {
                  const trips = localTrips[d.day] ?? [];
                  const rowBg = d.is_weekend
                    ? "bg-gray-50 dark:bg-gray-800/40"
                    : d.is_holiday
                      ? "bg-amber-50 dark:bg-amber-900/10"
                      : "bg-white dark:bg-gray-800";
                  const dateCls = d.is_weekend
                    ? "text-gray-400"
                    : "font-semibold text-gray-700 dark:text-gray-200";
                  const dayCls = d.is_weekend
                    ? "text-gray-400"
                    : "text-gray-500 dark:text-gray-400";
                  const updatedAtStr = formatDateTime(d.updated_at, "");

                  if (trips.length === 0) {
                    return (
                      <tr
                        key={d.day}
                        className={`border-t border-gray-100 dark:border-gray-700/50 ${rowBg}`}
                      >
                        <td className={`px-2 py-1.5 whitespace-nowrap ${dateCls}`}>
                          {d.date_str}
                        </td>
                        <td className={`px-2 py-1.5 ${dayCls}`}>{d.weekday_short}</td>
                        <td className="px-2 py-1" colSpan={6}>
                          {d.holiday_name && (
                            <span className="text-xs text-amber-700 dark:text-amber-400 italic">
                              {d.holiday_name}
                            </span>
                          )}
                        </td>
                        {!isProxy && (
                          <td className="px-2 py-1 text-[10px] text-gray-400 dark:text-gray-500 whitespace-nowrap">
                            {updatedAtStr}
                          </td>
                        )}
                        <td className="px-2 py-1">
                          <button
                            onClick={() => addTrip(d.day)}
                            title="Dodaj przejazd"
                            className="flex items-center gap-0.5 text-[10px] text-[var(--color-primary)] hover:underline"
                          >
                            <Plus size={9} /> dodaj
                          </button>
                        </td>
                      </tr>
                    );
                  }

                  return (
                    <Fragment key={d.day}>
                      {trips.map((trip, idx) => (
                        <tr
                          key={idx}
                          className={`border-t border-gray-100 dark:border-gray-700/50 ${idx === 0 ? rowBg : "bg-white dark:bg-gray-800"}`}
                        >
                          {/* Date + day — only on first trip row */}
                          {idx === 0 ? (
                            <>
                              <td className={`px-2 py-1 whitespace-nowrap ${dateCls}`}>
                                {d.date_str}
                              </td>
                              <td className={`px-2 py-1 ${dayCls}`}>{d.weekday_short}</td>
                            </>
                          ) : (
                            <>
                              <td className="px-2 py-1" />
                              <td className="px-2 py-1" />
                            </>
                          )}

                          {/* Trip fields */}
                          <td className="px-1 py-1">
                            <input
                              value={trip.purpose}
                              onChange={(e) => updateTrip(d.day, idx, "purpose", e.target.value)}
                              placeholder="cel wyjazdu"
                              className={inputCls}
                            />
                          </td>
                          <td className="px-1 py-1">
                            <input
                              value={trip.from_location}
                              onChange={(e) =>
                                updateTrip(d.day, idx, "from_location", e.target.value)
                              }
                              placeholder="skąd"
                              className={inputCls}
                            />
                          </td>
                          <td className="px-1 py-1">
                            <input
                              value={trip.to_location}
                              onChange={(e) =>
                                updateTrip(d.day, idx, "to_location", e.target.value)
                              }
                              placeholder="dokąd"
                              className={inputCls}
                            />
                          </td>
                          <td className="px-1 py-1">
                            <input
                              type="number"
                              step="0.1"
                              min="0"
                              value={trip.kilometers}
                              onChange={(e) =>
                                updateTrip(d.day, idx, "kilometers", e.target.value)
                              }
                              placeholder="0"
                              className={`${inputCls} text-right`}
                            />
                          </td>
                          <td className="px-1 py-1">
                            <input
                              type="number"
                              step="1"
                              min="0"
                              value={trip.odometer}
                              onChange={(e) =>
                                updateTrip(d.day, idx, "odometer", e.target.value)
                              }
                              placeholder="0"
                              className={`${inputCls} text-right`}
                            />
                          </td>
                          <td className="px-1 py-1">
                            <input
                              value={trip.notes}
                              onChange={(e) => updateTrip(d.day, idx, "notes", e.target.value)}
                              placeholder={d.holiday_name || "uwagi..."}
                              className={inputCls}
                            />
                          </td>

                          {/* Last change — only on first trip row; hidden in proxy mode */}
                          {!isProxy &&
                            (idx === 0 ? (
                              <td className="px-2 py-1 text-[10px] text-gray-400 dark:text-gray-500 whitespace-nowrap">
                                {updatedAtStr}
                              </td>
                            ) : (
                              <td className="px-2 py-1" />
                            ))}

                          {/* Actions */}
                          <td className="px-2 py-1">
                            <div className="flex items-center gap-1 justify-end">
                              {idx === trips.length - 1 && (
                                <button
                                  onClick={() => addTrip(d.day)}
                                  title="Dodaj kolejny przejazd"
                                  className="text-[var(--color-primary)] hover:text-blue-700 transition-colors"
                                >
                                  <Plus size={11} />
                                </button>
                              )}
                              <button
                                onClick={() => removeTrip(d.day, idx)}
                                title="Usuń przejazd"
                                className="text-gray-300 hover:text-red-400 transition-colors"
                              >
                                <X size={11} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : proxyOn && !proxyEmployeeId ? (
          <p className="text-sm text-gray-400 dark:text-gray-500 py-6 text-center">
            Wybierz pracownika, aby wygenerować kilometrówkę.
          </p>
        ) : null}

        {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
        {saveSuccess && (
          <p className="text-sm text-green-600 dark:text-green-400">Dane zostały zapisane.</p>
        )}

        <div className="flex gap-3 flex-wrap">
          <Button
            loading={downloading}
            onClick={downloadPdf}
            disabled={!data || isLoading}
          >
            <Download size={14} className="mr-1.5" /> Generuj PDF
          </Button>
          {!isProxy && (
            <Button
              variant="secondary"
              loading={saveMutation.isPending}
              onClick={() => {
                setError("");
                saveMutation.mutate();
              }}
              disabled={!data || isLoading}
            >
              <Save size={14} className="mr-1.5" /> Zapisz
            </Button>
          )}
          <Button variant="secondary" onClick={onClose}>
            Anuluj
          </Button>
        </div>
      </div>
    </Modal>
  );
}
